import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LongformPiece } from '../../data/showcasePieces';

interface LongformTakeoverProps {
  piece: LongformPiece;
  onClose: () => void;
}

const LongformTakeover: React.FC<LongformTakeoverProps> = ({ piece, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleRelatedClick = () => {
    if (!piece.related) return;
    onClose();
    navigate(`/${piece.related.slug}`);
  };

  // Portaled out for the same reason as ShowcaseModal — the showcase
  // pane creates a stacking context that would otherwise put the
  // takeover behind the deck chevrons.
  return createPortal(
    <div className="longform-takeover" role="dialog" aria-modal="true" aria-label={piece.title}>
      <header className="longform-takeover-header">
        <div className="longform-takeover-titles">
          <p className="longform-takeover-context">{piece.context}</p>
          <h1 className="longform-takeover-title">{piece.title}</h1>
        </div>
        <button
          className="longform-takeover-close"
          onClick={onClose}
          aria-label="Close"
        >
          Close
        </button>
      </header>

      <div className="longform-takeover-body">
        {piece.intro && (
          <p className="longform-takeover-intro">{piece.intro}</p>
        )}

        <div className="longform-takeover-pages">
          {piece.pages.map((page, i) => (
            <figure key={i} className="longform-takeover-page">
              <img src={page.src} alt={`${piece.title} — spread ${i + 1}`} />
              {page.caption && <figcaption>{page.caption}</figcaption>}
            </figure>
          ))}
        </div>

        {piece.related && (
          <div className="longform-takeover-footer">
            <button
              className="longform-takeover-related"
              onClick={handleRelatedClick}
            >
              {piece.related.hookCopy}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LongformTakeover;
